import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { 
  OrbitControls,
  Environment,
  BakeShadows,
  ContactShadows,
  Preload,
  CameraControls,
  useGLTF,
  useTexture,
  Center,
  PerspectiveCamera,
  useHelper,
  Html,
  Text,
  Float
} from '@react-three/drei';
import { EffectComposer, Bloom, SMAA, DepthOfField, Vignette } from '@react-three/postprocessing';
import { CustomizationOptions } from '../../types/customization';
import * as THREE from 'three';
import { useSpring, animated, config } from '@react-spring/three';
import { surfaceShapes } from '../../data/customization';
import { debounce, throttle } from '../../lib/utils';
import { Vector3, DirectionalLightHelper, SpotLightHelper } from 'three';
import { Leva, useControls } from 'leva';
import { Surface } from './Surface';

const materialPresets = {
  glossy: {
    roughness: 0.05,
    metalness: 0.2,
    envMapIntensity: 1.5,
    clearcoat: 0.8,
    clearcoatRoughness: 0.1,
    transmission: 0,
    thickness: 0,
    ior: 1.5,
    reflectivity: 1
  },
  matte: {
    roughness: 0.8,
    metalness: 0.1,
    envMapIntensity: 0.8,
    clearcoat: 0,
    clearcoatRoughness: 0.8,
    transmission: 0,
    thickness: 0,
    ior: 1.5,
    reflectivity: 0.5
  }
};

// Environment presets
const environmentPresets = [
  'apartment', 
  'city', 
  'dawn', 
  'forest', 
  'lobby', 
  'night', 
  'park', 
  'studio', 
  'sunset', 
  'warehouse'
];

// Lighting setup component
const SceneLighting = ({ intensity = 1.0, showHelpers = false }) => {
  const directionalLightRef = useRef<THREE.DirectionalLight>(null);
  const spotLightRef = useRef<THREE.SpotLight>(null);
  
  // Optional debug helpers
  if (showHelpers) {
    useHelper(directionalLightRef, DirectionalLightHelper, 1, 'red');
    useHelper(spotLightRef, SpotLightHelper, 'blue');
  }
  
  return (
    <>
      <ambientLight intensity={0.5 * intensity} />
      <directionalLight
        ref={directionalLightRef}
        position={[5, 5, 5]}
        intensity={0.8 * intensity}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-far={50}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
      />
      <spotLight
        ref={spotLightRef}
        position={[-5, 8, -5]}
        angle={0.3}
        penumbra={0.8}
        intensity={0.7 * intensity}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
      <pointLight position={[0, 6, 0]} intensity={0.5 * intensity} />
      
      {/* Add rim light to highlight edges */}
      <spotLight
        position={[0, 3, -8]}
        angle={0.5}
        penumbra={0.5}
        intensity={0.8 * intensity}
        color="#B4A06C"
        castShadow={false}
      />
    </>
  );
};

// Measurement annotations
const Measurements = ({ options }) => {
  const { width, length, thickness } = options;
  const w = width / 1000;
  const l = length / 1000;
  const t = thickness / 1000;
  
  return (
    <>
      {/* Width measurement */}
      <group position={[0, 0.01, -l/2 - 0.2]}>
        <Line 
          points={[[-w/2, 0, 0], [w/2, 0, 0]]} 
          color="#B4A06C" 
          lineWidth={1.5} 
        />
        <Text
          position={[0, 0, -0.05]}
          fontSize={0.05}
          color="#FFFFFF"
          anchorX="center"
          anchorY="top"
        >
          {width} mm
        </Text>
      </group>
      
      {/* Length measurement */}
      <group position={[-w/2 - 0.2, 0.01, 0]}>
        <Line 
          points={[[0, 0, -l/2], [0, 0, l/2]]} 
          color="#B4A06C" 
          lineWidth={1.5} 
        />
        <Text
          position={[-0.05, 0, 0]}
          fontSize={0.05}
          color="#FFFFFF"
          anchorX="right"
          anchorY="middle"
          rotation={[0, Math.PI/2, 0]}
        >
          {length} mm
        </Text>
      </group>
      
      {/* Thickness measurement */}
      <group position={[w/2 + 0.2, t/2, l/2 + 0.1]}>
        <Line 
          points={[[0, -t/2, 0], [0, t/2, 0]]} 
          color="#B4A06C" 
          lineWidth={1.5} 
        />
        <Text
          position={[0.05, 0, 0]}
          fontSize={0.05}
          color="#FFFFFF"
          anchorX="left"
          anchorY="middle"
        >
          {thickness} mm
        </Text>
      </group>
    </>
  );
};

// Line component for measurements
const Line = ({ points, color, lineWidth }) => {
  const lineGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry().setFromPoints(
      points.map(point => new THREE.Vector3(...point))
    );
    return geometry;
  }, [points]);
  
  return (
    <line geometry={lineGeometry}>
      <lineBasicMaterial attach="material" color={color} linewidth={lineWidth} />
    </line>
  );
};

// Camera controls component with improved orbit and zoom
const CameraController = ({ 
  controlsRef, 
  options, 
  onViewChange,
  isEdgeView,
  setIsEdgeView
}) => {
  const { camera, gl } = useThree();
  const width = (options.width || 1000) / 1000;
  const length = (options.length || 2000) / 1000;
  const shape = options.shape || 'rectangular';
  const prevShapeRef = useRef(shape);
  
  // Calculate optimal camera positions based on shape and dimensions
  const getCameraPositions = useMemo(() => {
    const w = width;
    const l = length;
    const maxDim = Math.max(w, l);
    
    // Calculate distance based on object size for proper framing
    // Use a multiplier that ensures the object fits well in the viewport
    const distanceMultiplier = 1.8; // Adjusted for better fit
    const distance = maxDim * distanceMultiplier;
    
    // Default positions - adjusted for better centering
    const positions = {
      default: [distance * 0.8, distance * 0.6, distance * 0.8], // Adjusted for better angle
      top: [0, distance * 1.2, 0],
      front: [0, distance * 0.2, distance],
      edge: [w/2 + 0.1, 0.05, 0.1]
    };
    
    // Adjust for specific shapes
    if (shape === 'round' || shape === 'oval') {
      positions.default = [distance * 0.8, distance * 0.6, distance * 0.8];
      positions.front = [0, 0, distance];
    } else if (shape === 'kidney' || shape === 'boat-shaped') {
      // These shapes need a slightly different angle to look good
      positions.default = [distance * 0.8, distance * 0.6, distance * 0.8];
    }
    
    return positions;
  }, [width, length, shape]);
  
  // Set initial camera position
  useEffect(() => {
    if (controlsRef.current) {
      const positions = getCameraPositions;
      
      // Center the camera target on the object
      const targetY = options.thickness / 2000; // Center vertically at half thickness
      
      controlsRef.current.setLookAt(
        positions.default[0],
        positions.default[1],
        positions.default[2],
        0, // target x - center horizontally
        targetY, // target y - center vertically
        0, // target z - center depth-wise
        true // animate
      );
      
      // Set proper zoom level based on object size
      const maxDim = Math.max(width, length);
      // Adjust zoom level to ensure the object fits well in the viewport
      const zoomLevel = 1 / (maxDim * 0.6);
      controlsRef.current.zoomTo(zoomLevel, true);
    }
  }, [options.shape, width, length, getCameraPositions, options.thickness]);
  
  // Reset camera when shape changes
  useEffect(() => {
    if (prevShapeRef.current !== shape && controlsRef.current) {
      // Shape has changed, reset camera to default view
      const positions = getCameraPositions;
      
      // Center the camera target on the object
      const targetY = options.thickness / 2000; // Center vertically at half thickness
      
      controlsRef.current.setLookAt(
        positions.default[0],
        positions.default[1],
        positions.default[2],
        0, // target x - center horizontally
        targetY, // target y - center vertically
        0, // target z - center depth-wise
        true // animate
      );
      
      // Reset edge view state
      setIsEdgeView(false);
      
      // Set proper zoom level based on object size
      const maxDim = Math.max(width, length);
      // Adjust zoom level to ensure the object fits well in the viewport
      const zoomLevel = 1 / (maxDim * 0.6);
      controlsRef.current.zoomTo(zoomLevel, true);
      
      // Update view mode
      if (onViewChange) {
        onViewChange('3d');
      }
    }
    
    prevShapeRef.current = shape;
  }, [shape, getCameraPositions, width, length, options.thickness, onViewChange, setIsEdgeView]);
  
  // Handle edge view changes
  useEffect(() => {
    if (isEdgeView && controlsRef.current) {
      const positions = getCameraPositions;
      controlsRef.current.setLookAt(
        positions.edge[0],
        positions.edge[1],
        positions.edge[2],
        width/2, // Focus on the edge
        0.05,
        0,
        true
      );
      
      // Zoom in for edge detail
      controlsRef.current.zoomTo(4, true);
      
      // Slightly rotate to show the edge better
      setTimeout(() => {
        controlsRef.current?.rotate(0.1, 0.05, true);
      }, 500);
    }
  }, [isEdgeView, getCameraPositions, width]);
  
  // Handle view mode changes
  const handleViewChange = useCallback((viewMode) => {
    if (controlsRef.current) {
      const positions = getCameraPositions;
      const targetY = options.thickness / 2000; // Center vertically at half thickness
      
      let position;
      
      switch (viewMode) {
        case 'top':
          position = positions.top;
          break;
        case 'front':
          position = positions.front;
          break;
        case '3d':
        default:
          position = positions.default;
      }
      
      controlsRef.current.setLookAt(
        position[0],
        position[1],
        position[2],
        0, // target x - center horizontally
        targetY, // target y - center vertically
        0, // target z - center depth-wise
        true // animate
      );
      
      if (viewMode !== '3d') {
        setIsEdgeView(false);
      }
      
      if (onViewChange) {
        onViewChange(viewMode);
      }
    }
  }, [getCameraPositions, onViewChange, setIsEdgeView, options.thickness]);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (controlsRef.current) {
        // Adjust camera on resize for responsive behavior
        const aspect = gl.domElement.clientWidth / gl.domElement.clientHeight;
        camera.aspect = aspect;
        camera.updateProjectionMatrix();
        
        // Re-center view
        handleViewChange('3d');
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [gl, camera, handleViewChange]);
  
  // Add double-click to reset view
  useEffect(() => {
    const handleDoubleClick = () => {
      handleViewChange('3d');
    };
    
    gl.domElement.addEventListener('dblclick', handleDoubleClick);
    return () => gl.domElement.removeEventListener('dblclick', handleDoubleClick);
  }, [gl, handleViewChange]);
  
  return null;
};

// Environment switcher
const EnvironmentController = ({ preset = 'apartment' }) => {
  return <Environment preset={preset} background={false} />;
};

// Post-processing effects
const PostProcessingEffects = ({ quality = 'medium' }) => {
  // Adjust effect parameters based on quality setting
  const bloomIntensity = quality === 'high' ? 0.5 : quality === 'medium' ? 0.3 : 0.2;
  const bloomRadius = quality === 'high' ? 0.7 : quality === 'medium' ? 0.5 : 0.3;
  
  return (
    <EffectComposer multisampling={quality === 'high' ? 8 : quality === 'medium' ? 4 : 0}>
      <SMAA />
      <Bloom
        intensity={bloomIntensity}
        luminanceThreshold={0.7}
        luminanceSmoothing={0.9}
        radius={bloomRadius}
      />
      <Vignette darkness={0.5} offset={0.5} />
      {quality === 'high' && (
        <DepthOfField
          focusDistance={0}
          focalLength={0.02}
          bokehScale={2}
          height={480}
        />
      )}
    </EffectComposer>
  );
};

interface TablePreviewCanvasProps {
  options: CustomizationOptions;
  materialTexture: string;
  finish: 'glossy' | 'matte';
  hideControls?: boolean;
  showMeasurements?: boolean;
  quality?: 'low' | 'medium' | 'high';
  environmentPreset?: string;
  onEdgeSelect?: () => void;
}

export const TablePreviewCanvas: React.FC<TablePreviewCanvasProps> = ({ 
  options, 
  materialTexture, 
  finish,
  hideControls = false,
  showMeasurements = false,
  quality = 'medium',
  environmentPreset = 'apartment',
  onEdgeSelect
}) => {
  const controlsRef = useRef<CameraControls>(null);
  const [isEdgeView, setIsEdgeView] = useState(false);
  const [showWireframe, setShowWireframe] = useState(false);
  const [showAxes, setShowAxes] = useState(false);
  const [activeViewMode, setActiveViewMode] = useState('3d');
  const [activeFinish, setActiveFinish] = useState<'glossy' | 'matte'>(finish);
  const previousEdgeProfile = useRef(options.edgeProfile);
  const previousShape = useRef(options.shape);
  const width = (options.width || 1000) / 1000;

  const material = useMemo(() => {
    const mat = new THREE.MeshPhysicalMaterial({
      ...materialPresets[activeFinish],
      map: materialTexture ? new THREE.TextureLoader().load(materialTexture) : null,
      side: THREE.FrontSide,
      shadowSide: THREE.FrontSide,
      wireframe: showWireframe
    });
    
    if (mat.map) {
      mat.map.wrapS = mat.map.wrapT = THREE.RepeatWrapping;
      mat.map.repeat.set(2, 2);
    }
    
    return mat;
  }, [activeFinish, materialTexture, showWireframe]);

  const handleEdgeView = () => {
    if (controlsRef.current) {
      const shapeConfig = surfaceShapes.find(s => s.id === options.shape);
      if (shapeConfig?.cameraPosition) {
        const edgeView = shapeConfig.cameraPosition.edge(width);
        controlsRef.current.setLookAt(
          edgeView[0],
          edgeView[1],
          edgeView[2],
          width/2, // Focus on the edge
          0.05,
          0,
          true
        );
        controlsRef.current.zoomTo(4, true);
      }
      setIsEdgeView(true);
      
      if (onEdgeSelect) {
        onEdgeSelect();
      }
    }
  };

  const handleDefaultView = () => {
    if (controlsRef.current) {
      const shapeConfig = surfaceShapes.find(s => s.id === options.shape);
      if (shapeConfig?.cameraPosition) {
        const defaultView = shapeConfig.cameraPosition.default;
        controlsRef.current.setLookAt(
          defaultView[0],
          defaultView[1],
          defaultView[2],
          0,
          options.thickness / 2000, // Look at the center of the object
          0,
          true
        );
      }
      setIsEdgeView(false);
    }
  };

  // Update local finish state when prop changes
  useEffect(() => {
    setActiveFinish(finish);
  }, [finish]);

  // Reset view when shape changes
  useEffect(() => {
    if (previousShape.current !== options.shape) {
      setIsEdgeView(false);
      handleDefaultView();
    }
    previousShape.current = options.shape;
  }, [options.shape]);

  // Show edge view when edge profile changes
  useEffect(() => {
    if (previousEdgeProfile.current !== options.edgeProfile) {
      handleEdgeView();
      
      if (controlsRef.current) {
        controlsRef.current.rotate(0.2, 0.1, true);
      }
    }
    previousEdgeProfile.current = options.edgeProfile;
  }, [options.edgeProfile]);

  // Performance optimizations
  const dpr = useMemo(() => {
    switch (quality) {
      case 'high': return [1, 2];
      case 'medium': return [1, 1.5];
      case 'low': return [0.8, 1];
      default: return [1, 1.5];
    }
  }, [quality]);

  // Calculate optimal camera settings based on object size
  const cameraSettings = useMemo(() => {
    const maxDimension = Math.max(
      options.width / 1000, 
      options.length / 1000
    );
    
    // Adjust FOV based on object size - smaller FOV for larger objects
    // A smaller FOV creates a more "zoomed in" effect
    const fov = 40 - (maxDimension * 2);
    
    // Adjust camera distance based on object size
    // Use a multiplier that ensures the object fits well in the viewport
    const distanceMultiplier = 1.8;
    const distance = maxDimension * distanceMultiplier;
    
    return {
      fov: Math.max(30, Math.min(fov, 45)), // Clamp between 30-45 degrees for better perspective
      position: [distance * 0.8, distance * 0.6, distance * 0.8], // Adjusted for better angle
      near: 0.1,
      far: Math.max(100, distance * 10)
    };
  }, [options.width, options.length]);

  return (
    <Canvas
      shadows={quality !== 'low'}
      dpr={dpr}
      camera={{ 
        position: cameraSettings.position,
        fov: cameraSettings.fov,
        near: cameraSettings.near,
        far: cameraSettings.far,
        up: [0, 1, 0] // Ensure correct orientation
      }}
      gl={{ 
        powerPreference: "high-performance",
        antialias: quality !== 'low',
        alpha: false,
        logarithmicDepthBuffer: true
      }}
      performance={{ min: 0.5 }}
    >
      <React.Suspense fallback={null}>
        {/* Center the object in the scene */}
        <Center>
          {/* Main Surface */}
          <Surface 
            options={options} 
            material={material}
            onEdgeSelect={handleEdgeView}
          />
        </Center>
        
        {/* Environment and Lighting */}
        <EnvironmentController preset={environmentPreset} />
        <SceneLighting intensity={1.2} showHelpers={false} />
        
        {/* Contact Shadows */}
        <ContactShadows
          position={[0, -0.01, 0]}
          scale={20}
          blur={2}
          far={4}
          opacity={0.7}
          frames={1}
        />
        
        {/* Measurement Annotations */}
        {showMeasurements && <Measurements options={options} />}
        
        {/* Axes Helper */}
        {showAxes && <axesHelper args={[2]} />}
        
        {/* Camera Controls */}
        <CameraController 
          controlsRef={controlsRef}
          options={options}
          onViewChange={setActiveViewMode}
          isEdgeView={isEdgeView}
          setIsEdgeView={setIsEdgeView}
        />
        <CameraControls
          ref={controlsRef}
          maxDistance={10}
          minDistance={0.5}
          maxPolarAngle={Math.PI / 1.75} // Prevent going below the floor
          minPolarAngle={0.1} // Prevent going directly above
          dampingFactor={0.1} // Smoother camera movement
          draggingDampingFactor={0.25}
          azimuthRotateSpeed={0.7} // Horizontal rotation speed
          polarRotateSpeed={0.5} // Vertical rotation speed
          dollySpeed={0.8} // Zoom speed
          boundaryFriction={0.6} // Smoother boundary behavior
          smoothTime={0.4} // Smoother transitions
          onDoubleClick={handleDefaultView}
          restThreshold={0.01} // More responsive controls
          enabled={true} // Always enable controls
          verticalDragToForward={false} // More intuitive dragging
          dollyToCursor={true} // Zoom towards cursor position
          infinityDolly={false} // Limit zoom distance
        />
        
        {/* Post-processing Effects */}
        <PostProcessingEffects quality={quality} />
        
        {/* Performance Optimizations */}
        <BakeShadows />
        <Preload all />
      </React.Suspense>
    </Canvas>
  );
};